import { useId, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useModalFocus } from './useModalFocus'
import './FirstVisitTutorial.css'

export const FIRST_VISIT_TUTORIAL_KEY = 'football-roguelike-tutorial-v1'

export function hasSeenFirstVisitTutorial(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(FIRST_VISIT_TUTORIAL_KEY) !== null
}

export function markFirstVisitTutorialSeen(storage: Pick<Storage, 'setItem'>): void {
  storage.setItem(FIRST_VISIT_TUTORIAL_KEY, 'seen')
}

interface FirstVisitTutorialProps {
  open: boolean
  onComplete: () => void
}

const TUTORIAL_STEPS = [
  {
    id: 'ovr',
    title: 'OVR',
    icon: 'mdi:star-four-points-outline',
    description: 'Tu carrera consiste en mejorar tu nivel sin descuidar físico, moral y prestigio. Un OVR alto aumenta tu rendimiento base.',
    cue: 'Crece al rendir bien y con algunas decisiones.',
  },
  {
    id: 'physical',
    title: 'Físico',
    icon: 'mdi:arm-flex-outline',
    description: 'Los encuentros consumen físico. Si baja de 70, tu OVR efectivo empieza a reducirse.',
    cue: 'Usa descansos y consumibles para llegar fresco.',
  },
  {
    id: 'morale',
    title: 'Moral',
    icon: 'mdi:heart-pulse',
    description: 'La confianza también modifica tu OVR efectivo. Los resultados y tus decisiones pueden subirla o bajarla.',
    cue: 'Por debajo de 60 empieza a penalizar el rendimiento.',
  },
  {
    id: 'reputation',
    title: 'Prestigio',
    icon: 'mdi:shield-star-outline',
    description: 'Es tu peso dentro del club: cuanto mayor sea, más probable es que juegues los partidos.',
    cue: 'Al fichar por otro equipo tendrás que reconstruirlo.',
  },
  {
    id: 'consumables',
    title: 'Objetos consumibles',
    icon: 'mdi:cards-playing-outline',
    description: 'Los objetos son de un solo uso: se eligen antes de un encuentro compatible para mejorar tu OVR efectivo, recuperar recursos o conseguir una ventaja concreta.',
    cue: 'Resérvalos para derbis, bosses o cuando el físico o la moral estén en riesgo.',
  },
  {
    id: 'relics',
    title: 'Reliquias',
    icon: 'mdi:diamond-stone',
    description: 'Duran toda la carrera y aplican su efecto automáticamente cuando cumples la condición indicada; no se gastan ni se activan a mano.',
    cue: 'Combina reliquias de una misma estrategia para activar sinergias y reforzar tu build.',
  },
] as const

export function FirstVisitTutorial({ open, onComplete }: FirstVisitTutorialProps) {
  const [stepIndex, setStepIndex] = useState(0)
  const titleId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const step = TUTORIAL_STEPS[stepIndex]
  const lastStep = stepIndex === TUTORIAL_STEPS.length - 1
  const nextStep = TUTORIAL_STEPS[stepIndex + 1]

  useModalFocus(open, dialogRef, { initialFocusSelector: '.button--primary', onEscape: onComplete })

  if (!open) return null

  return (
    <div className="dialog-backdrop tutorial-backdrop" role="presentation">
      <section ref={dialogRef} className="tutorial-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="tutorial-dialog__header">
          <span className={`tutorial-dialog__icon tutorial-dialog__icon--${step.id}`} aria-hidden="true"><Icon icon={step.icon} /></span>
          <div>
            <p className="eyebrow">Guía rápida · <span>{stepIndex + 1} de {TUTORIAL_STEPS.length}</span></p>
            <h2 id={titleId}>Cómo gestionar tu carrera</h2>
          </div>
        </header>

        <div className="tutorial-dialog__step" aria-live="polite">
          <h3>{step.title}</h3>
          <p>{step.description}</p>
          <aside><Icon icon="mdi:lightbulb-on-outline" aria-hidden="true" /><span>{step.cue}</span></aside>
        </div>

        <ol className="tutorial-dialog__progress" aria-label={`Paso ${stepIndex + 1} de ${TUTORIAL_STEPS.length}`}>
          {TUTORIAL_STEPS.map((item, index) => <li key={item.id} className={index === stepIndex ? 'is-current' : index < stepIndex ? 'is-complete' : ''} aria-current={index === stepIndex ? 'step' : undefined}><span>{item.title}</span></li>)}
        </ol>

        <footer className="tutorial-dialog__actions">
          <button className="tutorial-dialog__skip" type="button" onClick={onComplete}>Omitir tutorial</button>
          <span>
            {stepIndex > 0 && <button className="button tutorial-dialog__back" type="button" onClick={() => setStepIndex((current) => current - 1)}>Anterior</button>}
            <button className="button button--primary" type="button" aria-label={lastStep ? 'Entendido, empezar' : `Siguiente: ${nextStep.title}`} onClick={() => {
              if (lastStep) onComplete()
              else setStepIndex((current) => current + 1)
            }}>{lastStep ? 'Empezar' : 'Siguiente'}</button>
          </span>
        </footer>
      </section>
    </div>
  )
}
